import {SimpleApi, isApiError, isResolved, urlEncode, runWhile} from '@oc/utils'
import {
    AvailableGroup,
    CreateCustomerResponse,
    Customer,
    OffboardingResponse,
    ListCustomersResponse,
    Ecosystem,
    EcosystemOption,
    GetCreateCustomerProgressResponse,
    RegionCountry,
    S2aAccount,
    S2aAccountState,
    S2aService,
    OffboardingStatus,
    InactiveDeletedAccounts,
    DeletedAccountsResponse
} from './interfaces/Customer'

const gatewayApiName = 'IbtAPIGateway'
const ocPermissionGatewayApiName = 'oc-permissions'
const iamOperationsGatewayApiName = 'iamOperations'

export const ecosystemOptions: EcosystemOption[] = Object.entries(Ecosystem).map(([key, value]) => {
    return {label: key, value: value}
})

export class CustomerService {
    public async getCustomers(): Promise<Customer[]> {
        const customers: Array<Customer> = []
        let lastEvaluatedKey: string | undefined = undefined

        await runWhile(async () => {
            const queryParams: {[key: string]: string} = {}
            if (lastEvaluatedKey) {
                queryParams['lastEvaluatedKey'] = lastEvaluatedKey
            }

            const page = (await SimpleApi.get(gatewayApiName, '/customer', {queryParams})) as ListCustomersResponse

            lastEvaluatedKey = page.lastEvaluatedKey

            customers.push(...page.items)

            return !!lastEvaluatedKey
        }, 0)

        return customers
    }

    public async softlyDeactivateDeleteAccounts(s2aAccounts: InactiveDeletedAccounts, version: number): Promise<void> {
        const params = {body: {s2aAccounts, version}}
        return await SimpleApi.put(gatewayApiName, '/deleted-accounts', params)
    }

    public async getDeletedAccounts(): Promise<DeletedAccountsResponse> {
        return await SimpleApi.get(gatewayApiName, '/deleted-accounts')
    }

    public async getCustomerById(customerId: string): Promise<Customer> {
        const path = urlEncode`/customer/${customerId}`
        return await SimpleApi.get(gatewayApiName, path)
    }

    public async getCustomerInstanceIds(customerId: string): Promise<Map<string, string>> {
        const path = urlEncode`/customer/${customerId}/devices`
        const dictionary = await SimpleApi.get(gatewayApiName, path)
        return new Map<string, string>(Object.entries(dictionary))
    }

    public async getAvailableGroups(): Promise<AvailableGroup[]> {
        const response = await SimpleApi.get(gatewayApiName, '/equipment-services/service-list')
        return response.serviceGroups
    }

    public async getS2aAccount(accountId: string): Promise<S2aAccount | undefined> {
        try {
            return await SimpleApi.get(iamOperationsGatewayApiName, urlEncode`/accounts/${accountId}`)
        } catch (err) {
            if (isApiError(err) && err.response?.statusCode === 404) {
                return undefined
            }
            throw err
        }
    }

    public async getS2aAccounts(): Promise<S2aAccount[]> {
        return await SimpleApi.get(iamOperationsGatewayApiName, '/accounts')
    }

    public async setS2aAccountState(accountId: string, state: S2aAccountState.Active): Promise<S2aAccount>
    public async setS2aAccountState(
        accountId: string,
        state: S2aAccountState.Inactive,
        comment: string
    ): Promise<S2aAccount>
    public async setS2aAccountState(accountId: string, state: S2aAccountState, comment?: string): Promise<S2aAccount> {
        return await SimpleApi.put(iamOperationsGatewayApiName, `/accounts/${accountId}/${state}`, {
            body:
                state === S2aAccountState.Inactive
                    ? {
                          deactivateComment: comment,
                          deactivatedAt: new Date().toISOString()
                      }
                    : {}
        })
    }

    public async getS2aServices(): Promise<S2aService[]> {
        return await SimpleApi.get(iamOperationsGatewayApiName, '/services')
    }

    public async updateS2a(account: S2aAccount): Promise<S2aAccount> {
        const params = {body: account}
        return await SimpleApi.put(iamOperationsGatewayApiName, '/accounts', params)
    }

    public async createS2a(account: S2aAccount): Promise<CreateCustomerResponse> {
        const params = {body: account}
        return await SimpleApi.post(iamOperationsGatewayApiName, '/accounts', params)
    }

    public getCreateProgress(progressToken: string): Promise<GetCreateCustomerProgressResponse> {
        return SimpleApi.get(iamOperationsGatewayApiName, urlEncode`/accounts/createstatus/${progressToken}`)
    }

    public async getRegionCountryMapping(): Promise<RegionCountry[]> {
        return await SimpleApi.get(ocPermissionGatewayApiName, '/region-country')
    }

    public async update(customer: Customer, customerId: string): Promise<void> {
        const path = urlEncode`/customer/${customerId}`
        const params = {body: this.mapCustomerToRequestBodyParams(customer)}
        await SimpleApi.put(gatewayApiName, path, params)
    }

    public async updateFavoriteCustomers(usersFavoriteCustomers: string[]): Promise<void> {
        const path = urlEncode`/customer/favorite-customers`
        const params = {body: usersFavoriteCustomers}
        await SimpleApi.put(gatewayApiName, path, params)
    }

    public async getUsersFavoriteCustomers(): Promise<string[]> {
        const path = urlEncode`/customer/favorite-customers`
        return await SimpleApi.get(gatewayApiName, path)
    }

    public async createInOcPermissions(customerId: string, regionName: string): Promise<boolean> {
        const params = {body: {account: customerId, region: regionName}}
        return await isResolved(SimpleApi.post(ocPermissionGatewayApiName, '/account', params))
    }

    public async create(customer: Customer): Promise<void> {
        const params = {body: this.mapCustomerToRequestBodyParams(customer)}
        await SimpleApi.post(gatewayApiName, '/customer', params)
    }

    private mapCustomerToRequestBodyParams(customer: Customer) {
        return {
            s2aAccountName: customer.s2aAccountName,
            orderNumbersSyskron: customer.orderNumbersSyskron ?? [],
            quotationNumberKrones: customer.quotationNumberKrones || null,
            quotationNumberSyskron: customer.quotationNumberSyskron || null,
            street: customer.street || null,
            city: customer.city || null,
            stateName: customer.stateName || null,
            country: customer.country || null,
            zipCode: customer.zipCode || null,
            latitude: customer.latitude || null,
            longitude: customer.longitude || null,
            customerTimezone: customer.customerTimezone || null,
            salesRegion: customer.salesRegion || null,
            version: customer.version,
            customerNumber: customer.customerNumber || null,
            salesRepresentative: customer.salesRepresentative || null,
            accountProperties: customer.accountProperties,
            links: customer.links,
            readyKitCount: customer.readyKitCount,
            rkMonitoringHidden: customer.rkMonitoringHidden,
            preferredLanguages: customer.preferredLanguages ?? [],
            serviceGroups: customer.serviceGroups ?? []
        }
    }

    // Customer Offboarding

    public async getAccountsByState(state: S2aAccountState): Promise<S2aAccount[]> {
        const path = urlEncode`/accounts?state=${state}`
        return await SimpleApi.get(iamOperationsGatewayApiName, path)
    }

    public async triggerGdprDeletion(accountId: string): Promise<OffboardingResponse> {
        const path = urlEncode`/accounts/${accountId}/offboarding`
        return await SimpleApi.put(iamOperationsGatewayApiName, path, {body: {}})
    }

    public async restoreAccount(accountId: string): Promise<void> {
        const path = urlEncode`/accounts/${accountId}/restore`
        await SimpleApi.put(iamOperationsGatewayApiName, path, {body: {}})
    }

    public async getOffboardingStatus(accountId: string): Promise<OffboardingStatus> {
        const path = urlEncode`/account/${accountId}/status`
        return await SimpleApi.get(iamOperationsGatewayApiName, path)
    }

    public async extendOffboardingGracePeriod(accountId: string, offboardingDateISO: Date): Promise<void> {
        const path = urlEncode`/account/${accountId}/user-expiry`
        await SimpleApi.patch(iamOperationsGatewayApiName, path, {
            body: {usersDataDeleteAt: offboardingDateISO.toISOString()}
        })
    }
}
